import { Flex, Box, Heading, useColorModeValue } from "@chakra-ui/react";
import { BarkioOnly, BibinoOnly } from "src/utils/environment";
import BabyLottieAnimation from "src/modules/avatar/components/BabyLottieAnimation";
import CorrespondingAvatar from "src/modules/avatar/components/CorrespondingAvatar/CorrespondingAvatar";
import DogAvatar from "src/modules/avatar/components/DogAvatar";
import { Caption } from "src/atoms/typography";
import { AvatarModes } from "src/modules/avatar/enum";
import PreviewSelect from "./PreviewSelect";
import AvatarModeSelect from "./AvatarModeSelect";
import PhotoPicker from "./PhotoPicker";
import { Lottie } from "src/components/Lottie";
import getAnimationData from "src/utils/getAnimationData";

export default function AvatarPreview({
  avatar,
  preview,
  Previews,
  isSleeping,
  handlePreviewChange,
  changeAvatarMode,
  onAvatarSelection,
  croppedAvatar,
  t,
  ...rest
}) {
  const borderColor = useColorModeValue(
    "rgba(0, 0, 0, 0.12)",
    "rgba(255, 255, 255, 0.12)"
  );
  const {
    moreDataJson,
    avatarCustomization,
    sex,
    name,
    previewMode,
    profileImageFile
  } = avatar;

  const displayAvatar = previewMode === AvatarModes.illustration;
  const displayProfileImage =
    (previewMode === AvatarModes.profileImage && profileImageFile) ||
    (previewMode === AvatarModes.profileImage && croppedAvatar);
  const displayPicker =
    previewMode === AvatarModes.profileImage &&
    !profileImageFile &&
    !croppedAvatar;
  const displayString =
    previewMode === AvatarModes.illustration || displayProfileImage;

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      width="100%"
      px="24px"
      maxWidth="224px"
      maxHeight="336px"
      position="relative"
      height="100%"
      borderRadius="10px"
      border="1px solid"
      borderColor={borderColor}
      {...rest}
    >
      <Caption
        px="4px"
        transform="translateY(-50%)"
        pos="absolute"
        top="0%"
        left="12px"
        color="mediumEmphasis"
        bg="surfaceElevated"
      >
        {t("avatar.category.appearance")}
      </Caption>
      <AvatarModeSelect onChange={changeAvatarMode} mode={previewMode} />
      {displayAvatar && (
        <PreviewSelect
          t={t}
          Previews={Previews}
          isSleeping={isSleeping}
          onSelect={(newPreview) => handlePreviewChange(newPreview)}
        />
      )}
      <Box
        sx={{
          ".Bg": {
            path: {
              fill: "warningBase",
              fillOpacity: 1
            }
          }
        }}
      >
        {displayAvatar && (
          <>
            <BibinoOnly
              {...avatarCustomization}
              skinColor={
                avatarCustomization?.skinColor || moreDataJson?.skinColor
              }
              sex={sex}
              width="176px"
              height="128px"
              browserProps={{ useTheme: preview === Previews.Sleeping }}
              animationName={`${
                preview === Previews.Sleeping ? "sleeping" : "awake"
              }.json`}
            >
              {BabyLottieAnimation}
            </BibinoOnly>
            <BarkioOnly
              withSleepingAnimation={false}
              browserProps={{ useTheme: preview === Previews.Sleeping }}
              imageName={
                preview === Previews.Sleeping
                  ? `sleeping-${avatarCustomization.breedAppearance}.svg`
                  : `light-noisy-${avatarCustomization.breedAppearance}.svg`
              }
              width="176px"
              height="128px"
            >
              {DogAvatar}
            </BarkioOnly>
          </>
        )}

        {displayPicker && <PhotoPicker onChange={onAvatarSelection} />}
        {displayProfileImage && (
          <Box position="relative">
            <Lottie
              animationData={getAnimationData("zzz-small.json")}
              position="absolute"
              top="4px"
              right="4px"
              width="37px"
              transform="translateX(-50%)"
            />
            <CorrespondingAvatar
              mx="auto"
              avatar={avatar}
              customAvatar={croppedAvatar}
              height={104}
              width={104}
            />
          </Box>
        )}
        {displayString && (
          <Heading
            textAlign="center"
            mt={displayProfileImage ? "16px" : "8px"}
            fontSize="16px"
            fontWeight="500"
            lineHeight="1.5"
            letterSpacing="0.2px"
            color="highEmphasis"
            maxWidth="187px"
          >
            {isSleeping ? (
              <span>
                {name && isSleeping
                  ? t(sex === "male" ? "maleIsSleeping" : "femaleIsSleeping", {
                      value0: name
                    })
                  : t("avatarIsSleeping")}
              </span>
            ) : (
              <span>
                {name && !isSleeping
                  ? t(sex === "male" ? "maleIsAwake" : "femaleIsAwake", {
                      value0: name
                    })
                  : t("avatarIsAwake")}
              </span>
            )}
          </Heading>
        )}
      </Box>
    </Flex>
  );
}
